import React, { useEffect, useState } from "react";
import css from "./BulkUpdateDishesPricesComponent.module.css";
import { ButtonUpdate, Pagination } from "../../../ui";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { dishesActions } from "../../../store/slices/dishes-slices";
import { paginationActions } from "../../../store/slices/pagination-slices";
import Loader from "../../ui/Loader/Loader";
import { toast } from "react-toastify";
import { dishesService } from "../../../services/dishes-services";

function BulkUpdateDishesPricesComponent() {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { dishesA, trigger, filterParams, status } = useSelector(
    (store) => store.dishes
  );
  const { page } = useSelector((store) => store.pagination);
  const { register, handleSubmit, setValue } = useForm();
  const [dishPrices, setDishPrices] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const { totalPages, data: dishes, total } = dishesA || {};
  const currentLanguage = i18n.language;

  // Clear search when component mounts
  useEffect(() => {
    dispatch(dishesActions.clearFilters());
    setValue("dishesSearch", "");
  }, [dispatch, setValue]);

  // Fetch dishes when trigger, page, or filterParams change
  useEffect(() => {
    dispatch(dishesActions.getAllDishesPaginated({ page, ...filterParams }));
  }, [trigger, page, filterParams, dispatch]);

  // Initialize dish prices when dishes are loaded
  useEffect(() => {
    if (dishes && dishes.length > 0) {
      const initialPrices = [];
      dishes.forEach((dish) => {
        initialPrices.push({
          dishId: dish.id,
          price: dish.price || 0,
          cost: dish.cost || 0,
          updated: false,
        });
      });
      setDishPrices(initialPrices);
    }
  }, [dishes]);

  const submit = (data) => {
    // Reset pagination to page 1 when search changes
    dispatch(paginationActions.changePage(1));

    dispatch(
      dishesActions.setFilterParams({ word: data.dishesSearch.trim() || "" })
    );
    dispatch(
      dishesActions.getAllDishesPaginated({
        page: 1,
        ...filterParams,
        word: data.dishesSearch.trim() || "",
      })
    );
  };

  const handlePriceChange = (dishId, field, value) => {
    setDishPrices((dishes) =>
      dishes.map((dish) => {
        if (dish.dishId === dishId) {
          return { ...dish, [field]: parseFloat(value) || 0, updated: true };
        }
        return dish;
      })
    );
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const dishesToUpdate = dishPrices
        .filter((dish) => dish.updated)
        .map((dish) => ({
          dishId: dish.dishId,
          price: dish.price,
          cost: dish.cost,
        }));
      const response = await dishesService.updateBulkDishesPrices(
        dishesToUpdate
      );
      toast.success(response.message || "Dish prices updated successfully");
      dispatch(dishesActions.changeTrigger());
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setIsSaving(false);
    }
  };

  const isLoading = status === "Loading" || !dishesA;

  return (
    <section className={css.wrap}>
      <section className={css.menuBlock}>
        <form onChange={handleSubmit(submit)} className={css.form}>
          <input
            type="text"
            {...register("dishesSearch")}
            placeholder={t("Search") || "Search"}
          />
        </form>

        <ButtonUpdate onClick={handleSave} isLoading={isSaving} />
      </section>

      <section className={css.tableBlock}>
        <section className={css.header}>
          <div className={css.name}>{t("modal-name-input-new-category")}</div>
          <div className={css.price}>{t("modal-price")}</div>
          <div className={css.cost}>{t("Cost")}</div>
        </section>

        <div className={css.dishesContainer}>
          {isLoading ? (
            <div className={css.loadingContainer}>
              <Loader text={t("Loading dishes...")} />
            </div>
          ) : status === "rejected" ? (
            <div className={css.noResults}>{t("Error loading dishes")}</div>
          ) : dishes && dishes.length > 0 ? (
            dishes.map((dish) => {
              const displayName =
                dish.name?.[currentLanguage] || dish.name?.en || "";
              const currentPrice =
                dishPrices.find((price) => price.dishId === dish.id)?.price ??
                dish.price ??
                0;
              const currentCost =
                dishPrices.find((price) => price.dishId === dish.id)?.cost ??
                dish.cost ??
                0;

              return (
                <div key={dish.id} className={css.dishRow}>
                  <div className={css.name}>{displayName}</div>
                  <div className={css.price}>
                    <input
                      type="number"
                      value={currentPrice}
                      onChange={(e) =>
                        handlePriceChange(dish.id, "price", e.target.value)
                      }
                      className={css.inputField}
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div className={css.cost}>
                    <input
                      type="number"
                      value={currentCost}
                      onChange={(e) =>
                        handlePriceChange(dish.id, "cost", e.target.value)
                      }
                      className={css.inputField}
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>
              );
            })
          ) : (
            <div className={css.noResults}>
              {filterParams.word
                ? t("No dishes found matching your search")
                : t("No dishes available")}
            </div>
          )}
        </div>

        <div className={css.blockPagination}>
          <Pagination totalPages={totalPages || 0} totalItems={total || 0} />
        </div>
      </section>
    </section>
  );
}

export default BulkUpdateDishesPricesComponent;
